/**
 * Extract a YouTube video ID from any common YouTube URL form.
 *
 * Returns null if the URL is not a recognisable YouTube link.
 *
 * @param {string} url - Source video URL.
 * @returns {string|null} The 11-character video ID, or null.
 */
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

/**
 * Extract a Google Drive file ID from a /d/{id} URL or a bare ID string.
 *
 * @param {string} url - Source URL or file ID.
 * @returns {string|null} The file ID, or null.
 */
const getDriveFileId = (url) => {
  if (!url) return null;
  const driveMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch) return driveMatch[1];
  if (url.match(/^[a-zA-Z0-9_-]{20,}$/)) return url;
  return null;
};

/**
 * Build an embeddable iframe URL for a YouTube or Google Drive video.
 *
 * Falls back to the original URL when no provider is recognised.
 * When `autoplay` is true the YouTube embed URL includes the autoplay flag;
 * Drive's preview page does not honour autoplay reliably.
 *
 * @param {string} url - Source video URL.
 * @param {{autoplay?: boolean}} [options] - Optional flags.
 * @returns {string|null} An embed URL suitable for an iframe.
 */
export const getUniversalEmbedUrl = (url, options = {}) => {
  if (!url) return null;
  const { autoplay = false } = options;

  const youtubeId = getYouTubeVideoId(url);
  if (youtubeId) {
    const params = autoplay ? '?rel=0&autoplay=1' : '?rel=0';
    return `https://www.youtube.com/embed/${youtubeId}${params}`;
  }

  const driveId = getDriveFileId(url);
  if (driveId) return `https://drive.google.com/file/d/${driveId}/preview`;

  return url;
};

/**
 * Build a poster/thumbnail URL for a video without loading the full player.
 *
 * Used to defer iframe creation until the user clicks play, which avoids
 * rendering many iframes at once on list pages.
 *
 * @param {string} url - Source video URL.
 * @returns {string|null} A static image URL, or null when unsupported.
 */
export const getThumbnailUrl = (url) => {
  if (!url) return null;

  const youtubeId = getYouTubeVideoId(url);
  if (youtubeId) return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

  const driveId = getDriveFileId(url);
  if (driveId) return `https://drive.google.com/thumbnail?id=${driveId}&sz=w640`;

  return null;
};

/**
 * Convert a highlight type identifier to a human-readable label with emoji.
 *
 * @param {string} type - Highlight type from the API (e.g. "ace", "clutch_1v5").
 * @returns {string} Display label.
 */
export const formatHighlightType = (type) => {
  const types = {
    'ace': '🏆 ACE',
    'clutch_1v5': '🔥 1v5 CLUTCH',
    'clutch_1v4': '🔥 1v4 CLUTCH',
    'clutch_1v3': '🔥 1v3 CLUTCH',
    'clutch_1v2': '💪 1v2 CLUTCH',
    '4k': '⚡ 4K',
    '3k_style': '🎯 STYLISH 3K',
    'pistol_multi': '🔫 ECO HERO',
    'wallbang_multi': '🧱 WALLBANG KING',
    'knife_kill': '🔪 KNIFE KILL',
    'team_wipe': '💀 TEAM WIPE',
    'style_kill': '✨ STYLE KILL',
    'ninja_defuse': '🥷 NINJA DEFUSE'
  };
  if (!type) return '';
  return types[type] || type.toUpperCase();
};

/**
 * Convert a highlight tag identifier to a human-readable label with emoji.
 *
 * @param {string} tag - Tag identifier from the API.
 * @returns {string} Display label.
 */
export const formatTag = (tag) => {
  const tags = {
    'noscope': '🎯 No Scope',
    'wallbang': '🧱 Wallbang',
    'thrusmoke': '💨 Through Smoke',
    'blind': '🙈 While Blind'
  };
  return tags[tag] || tag;
};
